import { useRef, useLayoutEffect, useCallback } from 'react';
import { isVirtualPointerDownEvent, isKeyboardClick } from '../utils/event.js';
import { useInteractionHandler } from './use-interaction-handler.js';

function useCheckable({ mixed, inputRef, onChange: onChangeProp, recordingKey, actionOnPointerDown, ...props }) {
    const preventClick = useRef(false);
    const changeSource = useRef('mouse');
    useLayoutEffect(() => {
        inputRef.current.indeterminate = mixed ?? false;
    }, [inputRef, mixed]);
    const recordedOnChange = useInteractionHandler(onChangeProp, { eventName: 'change', recordingKey }, [onChangeProp]);
    // With actionOnPointerDown, a pointer gesture produces two clicks:
    // 1. onPointerDown calls click() synchronously to toggle the checkbox on press.
    // 2. The browser dispatches another click on release, which must be canceled.
    // preventClick identifies the release click. It is cleared in onClick because a controlled
    // rerender between React's capture and bubble listeners can restore checked before the change
    // plugin runs, causing React to dispatch onClick without the corresponding onChange.
    const onPointerDown = useCallback((e) => {
        // Since we want to trigger a change on pointer down, programmatically trigger a click here
        const isFromScreenReader = isVirtualPointerDownEvent(e.nativeEvent);
        if (!isFromScreenReader) {
            // NOTE(yan):
            //  from testing (macOS VoiceOver + Chrome), it seems like calling
            //    `.click()` when the over all action is done by a screen reader
            //    would result in this `.click()` call being ignored
            //  this meaning that the DOM `checked` state wouldn't toggle, and
            //    `onclick` and `onchange` events wouldn't fire
            //  to sidestep this issue, don't trigger an early click for screen
            //    reader users, and allow the normal browser click event to proceed
            e.currentTarget?.click();
            preventClick.current = true;
        }
    }, []);
    const onPointerLeave = useCallback(() => {
        preventClick.current = false;
    }, []);
    const actionOnPointerDownProps = { onPointerDown, onPointerLeave };
    // Space and Enter also dispatch click events. Their client coordinates are both zero,
    // which isKeyboardClick uses to distinguish them from mouse clicks.
    const onClick = useCallback((e) => {
        changeSource.current = isKeyboardClick(e) ? 'keyboard' : 'mouse';
        if (preventClick.current)
            e.preventDefault();
        preventClick.current = false;
    }, []);
    const onChange = useCallback((event) => {
        // React creates this change event before onClick runs. Its defaultPrevented value won't
        // reflect a cancellation in onClick, so check the shared native event instead.
        if (event.nativeEvent.defaultPrevented)
            return;
        const value = mixed ? true : event.currentTarget.checked;
        recordedOnChange?.(value, { event, source: changeSource.current });
    }, [mixed, recordedOnChange]);
    const { onBlur, onKeyDown, onKeyUp } = useEnterClick();
    const baseProps = {
        ...props,
        onClick,
        onChange,
        onKeyDown,
        onKeyUp,
        onBlur,
        // on webkit browsers, tabIndex is required for checkboxes to be focusable
        tabIndex: 0,
    };
    if (actionOnPointerDown) {
        return {
            ...baseProps,
            ...actionOnPointerDownProps,
        };
    }
    return baseProps;
}
/**
 * Sets up event handlers so that the element is clicked when releasing Enter, but only if Enter
 * was also pressed and released while this element remained focused. If enter is pressed, then
 * focus changes (even if focus returns to this element), then enter is released, the click will
 * not be triggered.
 */
function useEnterClick() {
    const receivedKeyDown = useRef(false);
    const onBlur = useCallback(() => {
        receivedKeyDown.current = false;
    }, []);
    const onKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            // non-standard behavior, usually hitting enter on a checkbox will cause it to submit the
            // form that contains it, but we'd rather prevent that behavior
            e.preventDefault();
            receivedKeyDown.current = true;
        }
    }, []);
    const onKeyUp = useCallback((e) => {
        if (e.key === 'Enter' && receivedKeyDown.current) {
            // non-standard behavior, instead of submitting the containing form, enter will toggle the
            // state of the checkbox when released.
            e.preventDefault();
            e.currentTarget.click();
        }
    }, []);
    return { onBlur, onKeyDown, onKeyUp };
}

export { useCheckable };
//# sourceMappingURL=use-checkable.js.map
